import React, { useContext } from 'react';
import { NamedLanguage as ReactNamedLanguage, LocalizeContext, LocalizeContextProps } from 'react-localize-redux';
import { renderToStaticMarkup } from 'react-dom/server';
import moment from 'moment';

import 'moment/locale/nl';
import 'moment/locale/en-gb';

import enTranslations from '@/translations/en.json';
import nlTranslations from '@/translations/nl.json';

import { ConfigProvider } from 'antd';

import enUS from 'antd/es/locale/en_US';
import enGB from 'antd/es/locale/en_GB';
import nlNL from 'antd/es/locale/nl_NL';
import { Locale } from 'antd/lib/locale-provider';
import qs from 'qs';

type NamedLanguage = ReactNamedLanguage & { antLocale: Locale };

const parsed = qs.parse(globalThis.window.location.search.slice(1));
const parsedLang = parsed['lang'] as string;
const parsedCountry = parsed['country'] as string;
const DefaultLanguage = parsedLang ? parsedLang : 'nl';
const DefaultCountry = parsedCountry ? parsedCountry :  (DefaultLanguage.toLowerCase() === 'en' ? 'gb' : 'nl');

export class Country {
    code: string;
}
export const country: Country = {code: DefaultCountry};

export const languages: NamedLanguage[] = [{
    name: 'Nederlands',
    code: 'nl-nl',
    antLocale: nlNL
}, {
    name: 'English',
    code: 'en-gb',
    antLocale: enGB
},
{
    name: 'English',
    code: 'en-us',
    antLocale: enUS
}];

export function useTranslate(): LocalizeContextProps {
    return useContext(LocalizeContext);
}

export function getLocale(): string {
    const result = (DefaultLanguage + '-' + DefaultCountry).toLowerCase();

    if (result !== 'nl-nl' && result !== 'en-gb' && result !== 'en-us') {
        return 'nl-nl';
    }
    return result;
}

export function useActiveLanguage(): [NamedLanguage, (language: NamedLanguage) => void] {
    const languageContext = useTranslate();

    const code = languageContext.activeLanguage?.code ?? getLocale();
    const activeLanguage = languages.find(l => l.code === code);

    return [
        activeLanguage,
        (language) => {
            moment.locale(language.code);
            languageContext.setActiveLanguage(language.code);
        }
    ];
}

const onMissingTranslation = ({ translationId, languageCode }): string => {
    return `Nada for ${translationId} - ${languageCode}`;
  };


export const LanguageProvider: React.FC = ({ children }) => {
    const languageContext = useTranslate();
    const [activeLanguage, setActiveLanguage] = useActiveLanguage();

    if (languageContext.languages.length === 0) {
        languageContext.initialize({
            languages: languages,
            options: { renderToStaticMarkup, onMissingTranslation }
        });

        languageContext.addTranslationForLanguage(enTranslations, 'en-gb');
        languageContext.addTranslationForLanguage(enTranslations, 'en-us');
        languageContext.addTranslationForLanguage(nlTranslations, 'nl-nl');
        languageContext.defaultLanguage = DefaultLanguage;

        setActiveLanguage(languages.find(l => l.code === getLocale()));
    }

    return (
        <ConfigProvider locale={activeLanguage.antLocale}>
            {children}
        </ConfigProvider>
    );
};